import type { ColoringEngine } from '../core/engine';
import type { NormPoint } from '../core/types';
import type { SaveStatus } from '../persistence/AutoSave';

/**
 * Developer overlay, enabled ONLY when the URL contains `?debug=1`.
 * It renders into a fixed DOM element and updates once per animation frame.
 * It is purely observational and is never included in exported PNGs (the export
 * composes from the artwork canvas layers, not the page).
 */
export interface DebugSources {
  engine: ColoringEngine;
  getPointer: () => { clientX: number; clientY: number; norm: NormPoint } | null;
  getSaveStatus: () => SaveStatus;
  getSwStatus: () => string;
  getViewport: () => { width: number; height: number };
}

export function isDebugEnabled(
  search: string = typeof location !== 'undefined' ? location.search : '',
): boolean {
  return new URLSearchParams(search).get('debug') === '1';
}

export class DebugOverlay {
  private el: HTMLElement;
  private raf = 0;
  private frames = 0;
  private lastFpsTime = performance.now();
  private fps = 0;
  private frameTimes: number[] = [];
  private lastFrame = performance.now();

  constructor(private readonly sources: DebugSources) {
    this.el = document.createElement('div');
    this.el.id = 'debug-overlay';
    document.body.appendChild(this.el);
    this.tick();
  }

  private tick = (): void => {
    const now = performance.now();
    const dt = now - this.lastFrame;
    this.lastFrame = now;
    this.frameTimes.push(dt);
    if (this.frameTimes.length > 120) this.frameTimes.shift();

    this.frames++;
    if (now - this.lastFpsTime >= 500) {
      this.fps = Math.round((this.frames * 1000) / (now - this.lastFpsTime));
      this.frames = 0;
      this.lastFpsTime = now;
    }

    this.el.textContent = this.compose();
    this.raf = requestAnimationFrame(this.tick);
  };

  private p95(): number {
    if (this.frameTimes.length === 0) return 0;
    const sorted = [...this.frameTimes].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
    return sorted[idx];
  }

  private compose(): string {
    const s = this.sources;
    const ptr = s.getPointer();
    const vp = s.getViewport();
    const dpr = typeof devicePixelRatio !== 'undefined' ? devicePixelRatio : 1;
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    return [
      `FPS         ${this.fps}`,
      `p95 frame   ${this.p95().toFixed(1)} ms`,
      `pointer     ${ptr ? `${ptr.clientX.toFixed(0)}, ${ptr.clientY.toFixed(0)}` : '-'}`,
      `norm        ${ptr ? `${ptr.norm.x.toFixed(3)}, ${ptr.norm.y.toFixed(3)}` : '-'}`,
      `region      ${s.engine.getActiveRegion() ?? '-'}`,
      `tool        ${s.engine.getTool()}`,
      `ops         ${s.engine.operationCount()}`,
      `save        ${s.getSaveStatus()}`,
      `sw          ${s.getSwStatus()}`,
      `net         ${online ? 'online' : 'offline'}`,
      `viewport    ${vp.width} x ${vp.height}`,
      `dpr         ${dpr}`,
    ].join('\n');
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.el.remove();
  }
}
